import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests (use ID token for Cognito)
api.interceptors.request.use((config) => {
  const storedTokens = localStorage.getItem('standLogic_tokens');
  if (storedTokens) {
    const tokens = JSON.parse(storedTokens);
    config.headers.Authorization = `Bearer ${tokens.idToken}`;
  }
  return config;
});

// Handle auth errors
let refreshPromise: Promise<boolean> | null = null;

// Uses a bare axios call so this request skips the interceptors; returns true on success.
async function refreshTokens(): Promise<boolean> {
  try {
    const stored = JSON.parse(localStorage.getItem('standLogic_tokens') || 'null');
    if (!stored?.refreshToken) return false;
    const res = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken: stored.refreshToken });
    localStorage.setItem('standLogic_tokens', JSON.stringify({ ...stored, ...res.data.tokens }));
    return true;
  } catch {
    return false;
  }
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;
    if (error.response?.status === 401 && original && !original._retried
        && !String(original.url).startsWith('/auth/')
        && localStorage.getItem('standLogic_tokens')) {
      original._retried = true;
      refreshPromise = refreshPromise || refreshTokens().finally(() => { refreshPromise = null; });
      if (await refreshPromise) {
        return api(original);
      }
    }
    // Sign-in failures are shown by the callback page; only expired sessions bounce home.
    if (error.response?.status === 401 && !String(original?.url).startsWith('/auth/')) {
      localStorage.removeItem('standLogic_tokens');
      localStorage.removeItem('standLogic_user');
      window.location.href = '/';
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  oauthToken: (code: string, redirectUri: string) => api.post('/auth/oauth/token', { code, redirectUri }),
};

export const huntingApi = {
  getOutlook: (zipcode: string) => api.get(`/hunting/outlook?zipcode=${zipcode}`),
  getPublicOutlook: (zipcode: string) => api.get(`/hunting/outlook/public?zipcode=${zipcode}`),
  getHourly: (zipcode: string, date: string) => api.get(`/hunting/hourly?zipcode=${zipcode}&date=${date}`),
  getMonthly: (zipcode: string, year: number, month: number) => api.get(`/hunting/monthly?zipcode=${zipcode}&year=${year}&month=${month}`),
  getLocation: (zipcode: string) => api.get(`/hunting/location?zipcode=${zipcode}`),
};

export const profileApi = {
  getProfile: () => api.get('/profile'),
  updateProfile: (data: {
    nickname?: string;
    zipcode?: string;
    locations?: { zipcode: string; name: string }[];
  }) => api.put('/profile', data),
  updateZipcode: (zipcode: string) => api.put('/profile/zipcode', { zipcode }),
  deleteAccount: () => api.delete('/profile'),
};

export interface BillingSubscription {
  id: string;
  status: string;
  plan: 'monthly' | 'annual';
  paidThrough: number;
  cancelAtPeriodEnd: boolean;
}

export interface BillingStatus {
  enabled: boolean;
  checkoutReady: boolean;
  complimentary: boolean;
  deleting?: boolean;
  subscriptions: BillingSubscription[];
}

export interface PlayBillingSubscription extends Omit<BillingSubscription, 'id'> {
  id: string;
  productId: string;
}

export interface PlayBillingStatus {
  enabled: boolean;
  subscriptions: PlayBillingSubscription[];
}

export const billingApi = {
  getStatus: () => api.get<BillingStatus>('/billing'),
  refresh: () => api.post<BillingStatus>('/billing/refresh'),
  checkout: (plan: 'monthly' | 'annual') => api.post<{ url: string }>('/billing/checkout', { plan }),
  portal: () => api.post<{ url: string }>('/billing/portal'),
};

export const playBillingApi = {
  getStatus: () => api.get<PlayBillingStatus>('/billing/play'),
  refresh: () => api.post<PlayBillingStatus>('/billing/play/refresh'),
  verify: (purchaseToken: string) =>
    api.post<{ subscription: PlayBillingSubscription; hasPlus: boolean }>('/billing/play/verify', { purchaseToken }),
};

export const adminApi = {
  listUsers: () => api.get('/admin/users'),
  changeUserTier: (username: string, tier: string) => api.put(`/admin/users/${username}/tier`, { tier }),
  deleteUser: (username: string) => api.delete(`/admin/users/${username}`),
  previewInactive: (days: number) => api.get('/admin/users/inactive', { params: { days } }),
  purgeInactive: (days: number) => api.post('/admin/users/purge-inactive', { days }),
};

export default api;
